import { useEffect, useMemo, type RefObject } from 'react';
import { getStatus, renderBadges } from '@/lib/card-status-cache';

/**
 * Hook that enriches rendered card name spans with GC/banned badges.
 *
 * After the container's innerHTML is set (via text-pipeline output),
 * this finds all `.decklist-card[data-card-name]` elements, batch-fetches
 * their status, and appends badge elements inline.
 *
 * @param containerRef - Ref to the DOM element containing rendered pipeline output
 * @param deps - Additional dependency values to trigger re-enrichment
 */
export function useCardStatusEnrich(
  containerRef: RefObject<HTMLElement | null>,
  deps: unknown[] = [],
): void {
  const depsKey = useMemo(() => JSON.stringify(deps), [deps]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const spans = container.querySelectorAll<HTMLElement>(
      '.decklist-card[data-card-name]',
    );
    if (!spans.length) return;

    // Collect unique names that don't already have adjacent badge elements
    const names = new Set<string>();
    for (const span of spans) {
      const name = span.getAttribute('data-card-name');
      if (name && !span.nextElementSibling?.classList.contains('dlw-indicators')) {
        names.add(name);
      }
    }
    if (!names.size) return;

    let cancelled = false;

    // Batch-fetch all uncached statuses
    const statusMap = new Map<string, { game_changer: boolean; banned: boolean; unreleased: boolean }>();
    Promise.all(
      Array.from(names).map(name =>
        getStatus(name)
          .then(s => statusMap.set(name, s))
          .catch(() => {}),
      ),
    ).then(() => {
      if (cancelled) return;

      for (const span of spans) {
        const name = span.getAttribute('data-card-name');
        if (!name) continue;
        if (span.nextElementSibling?.classList.contains('dlw-indicators')) continue;
        const badgeHtml = renderBadges(statusMap.get(name) ?? null);
        if (badgeHtml) span.insertAdjacentHTML('afterend', badgeHtml);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [containerRef, depsKey]);
}
